/**
 * PETALS currency graphic — a procedural flower "coin".
 *
 * Ported from the Shelf Garden Shopify app's Shard Mandala shape renderer
 * (`admin/src/pages/ShardMandalaPage.jsx` → drawShape / drawLotus / drawSpiralPetals / drawRose), so the
 * game's Petals icon matches the same procedural-flower language the pin/wheel system uses.
 *
 * A shape is described by a `PetalShapeConfig` (the same shape_config JSON the Shopify DB stores): a
 * `variant` plus petal count, layers, twist, and a fill (solid or an animated gradient). `drawPetalShape`
 * renders one to a 2D canvas context, centred, filling `radius`. Pure canvas — no game deps — so it can be
 * used anywhere on the storefront.
 */

export type PetalVariant = "lotus" | "spiralPetals" | "breathingLotus" | "rose";

export interface PetalShapeConfig {
  variant: PetalVariant;
  petals?: number;
  layers?: number;         // lotus: concentric rings, each smaller + rotated
  twist?: number;          // spiralPetals: per-petal rotation offset
  breathRate?: number;     // breathingLotus: pulse speed
  n?: number; d?: number;  // rose curve k = n/d
  fillStyle?: "solid" | "animatedGradient";
  fillColor?: string;
  strokeColor?: string;
  gradientColors?: string[];
  gradientSpeed?: number;
}

/** The canonical PETALS-currency coin: a soft pink lotus (matches the app's pink Petals theme #f9a8d4). */
export const PETALS_COIN: PetalShapeConfig = {
  variant: "lotus", petals: 8, layers: 2,
  fillStyle: "animatedGradient",
  gradientColors: ["#f9a8d4", "#ec4899", "#c026d3"],
  gradientSpeed: 0.0004,
  strokeColor: "#ffffff",
};

/**
 * Draw a petal shape centred in a `size`×`size` context. `t` is a monotonic time (ms) for animated fills /
 * breathing. Mirrors the Shopify renderer's geometry exactly.
 */
export function drawPetalShape(ctx: CanvasRenderingContext2D, size: number, cfg: PetalShapeConfig, t = 0): void {
  const cx = size / 2, cy = size / 2;
  const radius = size * 0.42;
  ctx.clearRect(0, 0, size, size);

  const fill = resolveFill(ctx, size, cfg, t);
  ctx.fillStyle = fill;
  ctx.strokeStyle = cfg.strokeColor || "#ffffff";
  ctx.lineWidth = Math.max(1, size * 0.012);

  const variant = cfg.variant || "lotus";
  const breath = variant === "breathingLotus" ? 1 + Math.sin(t * (cfg.breathRate ?? 0.02)) * 0.08 : 1;

  ctx.save();
  ctx.translate(cx, cy);

  switch (variant) {
    case "lotus":
    case "breathingLotus": {
      const layers = Math.max(1, cfg.layers ?? 2);
      for (let L = 0; L < layers; L++) {
        const lr = radius * breath * (1 - L * (0.32 / Math.max(1, layers)));
        ctx.save();
        ctx.rotate((L / layers) * (Math.PI / (cfg.petals ?? 8)));   // stagger rings into the gaps
        drawLotus(ctx, cfg.petals ?? 8, lr);
        ctx.restore();
      }
      // a small bright centre so it reads as a coin/flower heart
      ctx.beginPath();
      ctx.arc(0, 0, radius * 0.16, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.globalAlpha = 0.9;
      ctx.fill();
      ctx.globalAlpha = 1;
      break;
    }
    case "spiralPetals":
      drawSpiralPetals(ctx, cfg.petals ?? 12, radius, cfg.twist ?? 0.3);
      break;
    case "rose":
      drawRose(ctx, radius, cfg.n ?? 4, cfg.d ?? 1);
      break;
  }
  ctx.restore();
}

// --- resolve a fill: solid colour, or a slowly-rotating linear gradient (animatedGradient) ---
function resolveFill(ctx: CanvasRenderingContext2D, size: number, cfg: PetalShapeConfig, t: number): string | CanvasGradient {
  if (cfg.fillStyle === "animatedGradient" && cfg.gradientColors && cfg.gradientColors.length >= 2) {
    const speed = cfg.gradientSpeed ?? 0.0004;
    const ang = t * speed;
    const dx = Math.cos(ang) * size * 0.5, dy = Math.sin(ang) * size * 0.5;
    const g = ctx.createLinearGradient(size / 2 - dx, size / 2 - dy, size / 2 + dx, size / 2 + dy);
    const cols = cfg.gradientColors;
    for (let i = 0; i < cols.length; i++) g.addColorStop(i / (cols.length - 1), cols[i]!);
    return g;
  }
  return cfg.fillColor || "#f9a8d4";
}

// --- ported geometry (from ShardMandalaPage.jsx) ---
function drawLotus(ctx: CanvasRenderingContext2D, petals: number, radius: number): void {
  for (let i = 0; i < petals; i++) {
    ctx.save();
    ctx.rotate((i / petals) * Math.PI * 2);
    ctx.beginPath();
    ctx.ellipse(0, -radius * 0.5, radius * 0.2, radius * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
}

function drawSpiralPetals(ctx: CanvasRenderingContext2D, petals: number, radius: number, twist: number): void {
  for (let i = 0; i < petals; i++) {
    ctx.save();
    ctx.rotate((i / petals) * Math.PI * 2 + twist);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(radius * 0.4, -radius * 0.3, radius * 0.8, -radius * 0.1);
    ctx.quadraticCurveTo(radius * 0.4, radius * 0.1, 0, 0);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
}

function drawRose(ctx: CanvasRenderingContext2D, radius: number, n: number, d: number): void {
  ctx.beginPath();
  const k = n / d;
  for (let theta = 0; theta < Math.PI * 2 * d; theta += 0.02) {
    const r = radius * Math.cos(k * theta);
    const x = r * Math.cos(theta), y = r * Math.sin(theta);
    if (theta === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}
